<template>
  <div>
    <ModalComponent :show="show" @close="attemptClose" class="sunlight-dialog">
      <CardV2 variant="prompt" :title="$t('sunlightConditions')" :subtitle="$t('sunlightConditionsIntro')">
        <template #default>
          <div class="sunlight-dialog__layout">
            <div class="sunlight-dialog__main">
              <div class="sunlight-dialog__stage">
                <div
                  v-if="stageImage"
                  class="sunlight-dialog__stage-image"
                  :class="{
                    'sunlight-dialog__stage-image--uploading':
                      uploading && uploading.slot === selectedSlot && current === uploading.frame,
                  }"
                >
                  <img :src="stageImage.url" :alt="selectedSlot.time" />
                </div>

                <div
                  v-else
                  class="sunlight-dialog__stage-empty"
                  :class="{
                    'sunlight-dialog__stage-empty--uploading':
                      uploading && uploading.slot === selectedSlot && current === uploading.frame,
                  }"
                >
                  <img v-if="frameImage" :src="frameImage" class="sunlight-dialog__stage-fallback" />
                  <span class="sunlight-dialog__stage-hint">
                    <Icon :icon="['fal', 'sun']" />
                    <span>{{ $t('sunlightConditionsEmpty') }}</span>
                  </span>
                </div>

                <div v-if="selectedSlot" class="sunlight-dialog__stage-bar">
                  <span class="sunlight-dialog__stage-bar-label">{{ stageLabel }}</span>
                  <UploadProgress v-if="uploading" :value="uploadProgress" class="sunlight-dialog__stage-progress" />
                </div>
              </div>

              <FrameThumbnails :frames="frames" :current="current" @update:current="$emit('update:current', $event)">
                <template #caption="{ index }">
                  <span
                    v-if="data.slots.length"
                    class="sunlight-dialog__frame-count"
                    :class="{
                      'sunlight-dialog__frame-count--complete': coverage(index) === data.slots.length,
                      'sunlight-dialog__frame-count--partial': coverage(index) && coverage(index) < data.slots.length,
                    }"
                  >
                    <Icon v-if="coverage(index) === data.slots.length" :icon="['fal', 'check']" />
                    {{ coverage(index) }}/{{ data.slots.length }}
                  </span>
                </template>
              </FrameThumbnails>
            </div>

            <div class="sunlight-dialog__controls">
              <Tabs v-model="activeSeason" :items="tabItems" theme="border" class="sunlight-dialog__tabs">
                <template v-for="group in groups" v-slot:[`${group.key}-label`]>
                  <span :key="`${group.key}-label`" class="sunlight-dialog__tab-label">
                    {{ group.label }}
                    <span v-if="group.slots.length" class="sunlight-dialog__tab-count">{{ group.slots.length }}</span>
                  </span>
                </template>

                <template #[activeSeason]>
                  <div class="sunlight-dialog__slots">
                    <div
                      v-for="{ slot, index } in activeGroup.slots"
                      :key="index"
                      class="sunlight-dialog__slot"
                      :class="{ 'sunlight-dialog__slot--selected': index === selected }"
                    >
                      <div
                        class="sunlight-dialog__slot-row"
                        role="button"
                        tabindex="0"
                        @click="select(index)"
                        @keypress.enter="select(index)"
                      >
                        <div
                          class="sunlight-dialog__slot-thumb"
                          :class="{
                            'sunlight-dialog__slot-thumb--empty': !slot.images[current],
                            'sunlight-dialog__slot-thumb--uploading':
                              uploading && uploading.slot === slot && current === uploading.frame,
                            'sunlight-dialog__slot-thumb--dragover': dragFillTargets.includes(index) && dragValid,
                            'sunlight-dialog__slot-thumb--dragover-invalid':
                              dragFillTargets.includes(index) && !dragValid,
                          }"
                          @click.stop="slot.images[current] ? select(index) : upload(index)"
                          @dragover="onTileDragOver($event, index)"
                          @dragleave="onTileDragLeave($event, index)"
                          @drop="onTileDrop($event, index)"
                        >
                          <img v-if="slot.images[current]" :src="slot.images[current].url" :alt="slot.time" />
                          <template v-else>
                            <Icon :icon="['fal', 'arrow-to-top']" />
                            <span>{{ $t('upload') }}</span>
                            <span v-if="dimensionsHint" class="sunlight-dialog__slot-thumb-hint">{{
                              dimensionsHint
                            }}</span>
                          </template>
                          <!-- .stop on every button: the row re-renders mid-bubble when the
                               image disappears, so the thumb handler would re-evaluate an
                               emptied slot and open the file picker -->
                          <span class="sunlight-dialog__slot-actions" @click.stop>
                            <button
                              type="button"
                              class="sunlight-dialog__slot-default overlay-icon"
                              :class="{ 'sunlight-dialog__slot-default--active': slot.isDefault }"
                              v-tooltip="$t('preferredDate')"
                              @click.stop="setDefault(index)"
                            >
                              <Icon :icon="['fas', 'star']" />
                            </button>
                            <template v-if="slot.images[current]">
                              <button
                                type="button"
                                class="sunlight-dialog__slot-image-remove overlay-icon overlay-icon--error"
                                v-tooltip="$t('delete')"
                                @click.stop="removeImage(index)"
                              >
                                <Icon icon="trash" />
                              </button>
                              <button
                                type="button"
                                class="sunlight-dialog__slot-image-download overlay-icon overlay-icon--primary"
                                v-tooltip="$t('download')"
                                @click.stop="download(slot)"
                              >
                                <Icon icon="arrow-alt-circle-down" />
                              </button>
                            </template>
                          </span>
                        </div>

                        <span class="sunlight-dialog__slot-time">
                          <input type="time" v-model="slot.time" @click.stop="openTimePicker" @change="resort" />
                        </span>

                        <button
                          type="button"
                          class="sunlight-dialog__slot-delete"
                          v-tooltip="deleteTooltip"
                          @click.stop="askDelete(index)"
                        >
                          <Icon :icon="['fal', 'trash']" />
                        </button>
                      </div>

                      <p v-if="!slot.time" class="sunlight-dialog__slot-warning">
                        {{ $t('sunlightConditionsTimeMissing') }}
                      </p>

                      <p v-else-if="isDuplicate(slot)" class="sunlight-dialog__slot-warning">
                        {{ $t('sunlightConditionsAlreadyExists') }}
                      </p>

                      <p
                        v-else-if="error && error.slot === slot && error.frame === current"
                        class="sunlight-dialog__slot-warning"
                      >
                        {{ error.message }}
                      </p>
                    </div>

                    <div
                      class="sunlight-dialog__slot sunlight-dialog__slot--add"
                      :class="{
                        'sunlight-dialog__slot--dragover': dragTarget === 'add' && dragValid,
                        'sunlight-dialog__slot--dragover-invalid': dragTarget === 'add' && !dragValid,
                      }"
                      role="button"
                      tabindex="0"
                      @click="addSlot(activeSeason)"
                      @keypress.enter="addSlot(activeSeason)"
                      @dragover="onTileDragOver($event, 'add')"
                      @dragleave="onTileDragLeave($event, 'add')"
                      @drop="onTileDrop($event, 'add')"
                    >
                      <div class="sunlight-dialog__slot-row">
                        <div class="sunlight-dialog__slot-thumb sunlight-dialog__slot-thumb--add">
                          <Icon :icon="['fal', 'plus']" />
                        </div>
                        <span class="sunlight-dialog__slot-add-label">{{ $t('addSunlightConditions') }}</span>
                        <span class="sunlight-dialog__slot-add-placeholder"></span>
                      </div>
                    </div>
                  </div>
                </template>
              </Tabs>
            </div>
          </div>
        </template>

        <template #actions>
          <ButtonComponent :label="$t('cancel')" :icon="['fal', 'times']" @click="attemptClose" />
          <ButtonComponent
            :label="$t('sunlightConditionsResetAll')"
            :icon="['fal', 'trash']"
            @click="showResetAll = true"
          />
          <span v-if="hasDuplicates" class="sunlight-dialog__footer-warning">
            {{ $t('sunlightConditionsAlreadyExists') }}
          </span>

          <ButtonComponent
            theme="primary"
            :class="{ 'elder-button--disabled': !canSave }"
            v-tooltip="saveTooltip"
            :label="$t('confirm')"
            :icon="['fal', 'arrow-to-bottom']"
            @click="save"
          />
        </template>
      </CardV2>
    </ModalComponent>

    <UnsavedChangesDialog :show="showDiscard" @confirm="discard" @cancel="showDiscard = false" />

    <ConfirmDialog
      :show="deleteIndex !== null"
      :title="$t('deleteSunlightConditions')"
      :message="$t('deleteSunlightConditionsWithAllAssociations')"
      :confirmLabel="$t('delete')"
      :confirmIcon="['fal', 'trash']"
      @confirm="removeSlot(deleteIndex)"
      @cancel="deleteIndex = null"
    />

    <ConfirmDialog
      :show="showResetAll"
      :title="$t('sunlightConditionsResetAll')"
      :message="$t('resetAllSunlightConditions')"
      :confirmLabel="$t('confirm')"
      :confirmIcon="['fal', 'trash']"
      @confirm="confirmResetAll"
      @cancel="showResetAll = false"
    />
  </div>
</template>

<script>
import { ButtonComponent } from 'vue-elder-button'
import { ModalComponent } from 'vue-elder-modal'
import CardV2 from '@/components/Card-v2'
import Tabs from '@/components/Tabs'
import UnsavedChangesDialog from '@/components/UnsavedChangesDialog'
import ConfirmDialog from '@/components/ConfirmDialog'
import FrameThumbnails from './FrameThumbnails'
import { Capitalize, DownloadUrl } from '@/utils'
import { uploadImages, pickFiles } from './upload'
import { formatDimensions } from './dimensions'
import { framesToSlots, slotDate, minutesFromDawn, byMonthAndTime, DAY_START_MINUTES } from './conditions'
import TileDropzone from './tileDropzone.mixin'
import UploadProgress from './UploadProgress.vue'

export default {
  mixins: [TileDropzone],
  props: {
    show: Boolean,
    frames: {
      type: Array,
      default: () => [],
    },
    current: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      data: { slots: [] },
      snapshot: '',
      selected: 0,
      activeSeason: null,
      deleteIndex: null,
      showDiscard: false,
      showResetAll: false,
      uploading: null,
      uploadProgress: 0,
      error: null,
    }
  },
  computed: {
    dropDisabled() {
      return Boolean(this.uploading)
    },
    dragFillTargets() {
      if (typeof this.dragTarget !== 'number') return []
      const start = this.data.slots[this.dragTarget]
      if (!start) return []
      if (start.images[this.current]) return [this.dragTarget]
      const ordered = this.data.slots
        .map((slot, index) => ({ slot, index }))
        .filter(entry => entry.slot.month === start.month)
        .sort((a, b) => minutesFromDawn(a.slot.time) - minutesFromDawn(b.slot.time))
      const from = ordered.slice(ordered.findIndex(entry => entry.index === this.dragTarget))
      return from.slice(0, this.dragCount).map(entry => entry.index)
    },
    frameImage() {
      return this.$path('image.url', this.frames[this.current])
    },
    dimensionsHint() {
      return formatDimensions(this.frames)
    },
    deleteTooltip() {
      if (this.frames.length > 1) return this.$t('removeSunlightConditionsFromAllFrames')
      return this.$t('delete')
    },
    selectedSlot() {
      return this.data.slots[this.selected]
    },
    stageImage() {
      return this.selectedSlot && this.selectedSlot.images[this.current]
    },
    stageLabel() {
      const time = `${this.$t('clockAt')} ${this.selectedSlot.time}`
      if (this.frames.length < 2) return time
      return `${this.$t('sunlightConditionsFrameCounter', {
        current: this.current + 1,
        total: this.frames.length,
      })} · ${time}`
    },

    groups() {
      const year = new Date().getFullYear()
      const seasons = [
        { month: '03', label: this.$t('spring') },
        { month: '06', label: this.$t('summer') },
        { month: '09', label: this.$t('autumn') },
        { month: '12', label: this.$t('winter') },
      ]

      const groups = seasons.map(season => {
        const existing = this.data.slots.find(slot => slot.month.slice(5) === season.month)
        return { key: existing ? existing.month : `${year}-${season.month}`, label: season.label }
      })

      this.data.slots.forEach(slot => {
        if (groups.some(group => group.key === slot.month)) return
        groups.push({ key: slot.month, label: this.monthLabel(slot.month) })
      })

      return groups.map(group => ({
        ...group,
        slots: this.data.slots.map((slot, index) => ({ slot, index })).filter(entry => entry.slot.month === group.key),
      }))
    },
    tabItems() {
      return this.groups.map(group => ({ id: group.key, label: group.label }))
    },
    activeGroup() {
      return this.groups.find(group => group.key === this.activeSeason) || { slots: [] }
    },
    duplicates() {
      const counts = {}
      this.data.slots.forEach(slot => {
        const key = `${slot.month}|${slot.time}`
        counts[key] = (counts[key] || 0) + 1
      })
      return counts
    },
    hasDuplicates() {
      return Object.values(this.duplicates).some(count => count > 1)
    },
    hasEmptyTimes() {
      return this.data.slots.some(slot => !slot.time)
    },
    saveTooltip() {
      if (!this.hasChanges) return this.$t('noUnsavedChanges')
      return null
    },
    hasChanges() {
      return JSON.stringify(this.data) !== this.snapshot
    },
    canSave() {
      return this.hasChanges && !this.uploading && !this.hasDuplicates && !this.hasEmptyTimes
    },
  },
  watch: {
    show(open) {
      if (!open) return

      const slots = framesToSlots(this.frames)

      this.data = { slots }
      this.snapshot = JSON.stringify(this.data)
      this.selected = 0
      this.deleteIndex = null
      this.showDiscard = false
      this.uploading = null
      this.uploadProgress = 0
      this.error = null

      const filled = this.groups.find(group => group.slots.length)
      this.activeSeason = (filled || this.groups[1]).key
    },
    activeSeason(key) {
      const group = this.groups.find(entry => entry.key === key)
      if (!group) return
      this.selected = group.slots.length ? group.slots[0].index : -1
    },
  },
  methods: {
    monthLabel(key) {
      if (!key) return ''
      const [year, month] = key.split('-')
      const label = new Date(Number(year), Number(month) - 1, 1).toLocaleDateString(this.$i18n.locale, {
        month: 'long',
      })
      return Capitalize(label)
    },
    openTimePicker({ target }) {
      if (!target.showPicker) return
      target.showPicker()
    },
    coverage(index) {
      return this.data.slots.filter(slot => slot.images[index]).length
    },
    isDuplicate(slot) {
      return this.duplicates[`${slot.month}|${slot.time}`] > 1
    },
    select(index) {
      this.selected = index
    },
    attemptClose() {
      if (this.showDiscard || this.deleteIndex !== null || this.showResetAll) return
      if (!this.hasChanges) return this.$emit('close')
      this.showDiscard = true
    },
    discard() {
      this.showDiscard = false
      this.$emit('close')
    },
    resort() {
      const slot = this.selectedSlot
      this.data.slots.sort(byMonthAndTime)
      this.selected = Math.max(this.data.slots.indexOf(slot), 0)
      this.deleteIndex = null
    },
    setDefault(index) {
      this.data.slots.forEach((slot, i) => (slot.isDefault = i === index))
    },
    resetAll() {
      this.data.slots = []
    },
    confirmResetAll() {
      this.resetAll()
      this.showResetAll = false
    },
    getNextFreeTime(month) {
      const pad = n => String(n).padStart(2, '0')
      const at = min => `${pad(Math.floor(min / 60))}:${pad(min % 60)}`
      const times = this.data.slots.filter(slot => slot.month === month).map(slot => slot.time)
      if (!times.length) return at(DAY_START_MINUTES)

      // Latest slot in day-arc order + 1 hour (wraps: 05:00 → 06:00, 23:00 → 00:00).
      const last = times.reduce((a, b) => (minutesFromDawn(b) > minutesFromDawn(a) ? b : a))
      const [hours, minutes] = last.split(':').map(Number)
      return at((hours * 60 + minutes + 60) % 1440)
    },
    addSlot(month) {
      const slot = {
        time: this.getNextFreeTime(month),
        month,
        isDefault: !this.data.slots.length,
        images: this.frames.map(() => null),
      }

      this.data.slots.push(slot)
      this.resort()
      this.selected = this.data.slots.indexOf(slot)

      this.$nextTick(() => {
        const input = this.$el.querySelector('.sunlight-dialog__slot--selected input')
        if (!input) return
        input.focus()
      })
    },
    askDelete(index) {
      const slot = this.data.slots[index]
      if (!slot.images.some(Boolean)) return this.removeSlot(index)
      this.deleteIndex = index
    },
    removeSlot(index) {
      const selectedSlot = this.selectedSlot
      const removed = this.data.slots.splice(index, 1)[0]
      if (removed.isDefault && this.data.slots.length) this.data.slots[0].isDefault = true

      this.selected = Math.max(this.data.slots.indexOf(selectedSlot), 0)
      this.deleteIndex = null

      // A legacy-year season can disappear entirely when its last slot goes
      if (!this.groups.some(group => group.key === this.activeSeason)) this.activeSeason = this.groups[1].key
    },
    removeImage(index) {
      const slot = this.data.slots[index]
      if (!slot) return
      this.$set(slot.images, this.current, null)
    },
    download(slot) {
      const image = slot.images[this.current]
      if (!image) return
      DownloadUrl(image.url, image.name)
    },
    upload(index) {
      const startSlot = this.data.slots[index]
      if (!startSlot || this.uploading) return
      this.selected = index
      return pickFiles({ multiple: true }).then(files => this.runUpload(startSlot, this.current, files))
    },
    dropAccepts(target, count) {
      const slot = typeof target === 'number' ? this.data.slots[target] : null
      return !(slot && slot.images[this.current]) || count === 1
    },
    onDropFiles(target, files) {
      if (target === 'add') return this.addAndFill(files)
      const slot = this.data.slots[target]
      if (!slot) return
      if (slot.images[this.current]) {
        if (files.length === 1) this.replaceSlotImage(target, files)
      } else {
        this.runUpload(slot, this.current, files)
      }
    },
    addAndFill(files) {
      this.addSlot(this.activeSeason)
      this.runUpload(this.selectedSlot, this.current, files)
    },
    formatUploadError(raw, slot, frame) {
      if (!raw) return null
      const message = raw.count ? this.$tc('uploadImageDimensionsMismatch', raw.count, raw) : raw.message
      return { message, slot, frame }
    },
    replaceSlotImage(index, files) {
      const slot = this.data.slots[index]
      if (!slot || !files.length) return

      const frame = this.current
      this.uploading = { slot, frame }
      this.uploadProgress = 0
      this.error = null

      return uploadImages(files.slice(0, 1), this.frames, p => (this.uploadProgress = p))
        .then(({ accepted, error }) => {
          this.error = this.formatUploadError(error, slot, frame)

          const image = accepted[0]
          if (!image) return
          this.$set(slot.images, frame, image)
        })
        .finally(() => {
          this.uploading = null
        })
    },
    runUpload(startSlot, frame, files) {
      if (!startSlot || this.uploading || !files.length) return

      const month = startSlot.month
      this.uploading = { slot: startSlot, frame }
      this.uploadProgress = 0
      this.error = null

      return uploadImages(files, this.frames, p => (this.uploadProgress = p))
        .then(({ accepted, error }) => {
          this.error = this.formatUploadError(error, startSlot, frame)
          if (!accepted.length) return

          const targets = this.data.slots
            .filter(slot => slot.month === month)
            .sort((a, b) => minutesFromDawn(a.time) - minutesFromDawn(b.time))
          const fromClicked = targets.slice(targets.indexOf(startSlot))

          accepted.forEach((image, i) => {
            let slot = fromClicked[i]
            if (!slot) {
              slot = {
                time: this.getNextFreeTime(month),
                month,
                isDefault: false,
                images: this.frames.map(() => null),
              }
              this.data.slots.push(slot)
            }
            this.$set(slot.images, frame, image)
          })

          if (!this.data.slots.some(slot => slot.isDefault)) this.data.slots[0].isDefault = true

          this.resort()
          this.selected = this.data.slots.indexOf(startSlot)
        })
        .finally(() => {
          this.uploading = null
        })
    },
    save() {
      if (!this.canSave) return

      this.frames.forEach((frame, index) => {
        const conditions = this.data.slots
          .filter(slot => slot.images[index])
          .map(slot => ({
            image: slot.images[index],
            date: slotDate(slot.month),
            time: slot.time,
            isDefault: slot.isDefault,
          }))

        this.$set(frame, 'sunlightConditions', conditions)
      })

      this.$emit('close')
    },
  },
  components: {
    ButtonComponent,
    ModalComponent,
    CardV2,
    Tabs,
    UnsavedChangesDialog,
    ConfirmDialog,
    FrameThumbnails,
    UploadProgress,
  },
}
</script>

<style lang="scss">
$thumb-width: 112px;

.sunlight-dialog__stage-progress {
  flex: 1;
  margin-left: 0.75rem;
}
$thumb-height: 70px;

.sunlight-dialog {
  .card-v2 {
    width: 1440px;
    max-width: 96vw;
  }

  .card-v2__actions {
    justify-content: flex-start;

    > :last-child {
      margin-left: auto;
    }
  }

  &__layout {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    gap: 1.25rem;
  }

  &__main {
    flex: 0 1 500px;
    min-width: 320px;
  }

  &__stage-image {
    img {
      width: 100%;
      border-radius: var(--border-radius-sm);
      display: block;
    }

    &--uploading {
      opacity: 0.5;
      pointer-events: none;
    }
  }

  &__stage-bar {
    margin-top: 0.5rem;
  }

  &__stage-bar-label {
    font-size: 0.85em;
    color: var(--dark-grey);
  }

  &__stage-empty {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 260px;
    overflow: hidden;
    border: 1px dashed var(--grey);
    border-radius: var(--border-radius-sm);
    background-color: var(--light-grey);

    &--uploading {
      opacity: 0.5;
    }
  }

  &__stage-fallback {
    width: 100%;
    display: block;
    opacity: 0.25;
  }

  &__stage-hint {
    position: absolute;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
    padding: 1rem;
    color: var(--darkest-grey);
    font-size: 0.9em;
    font-weight: 500;
    text-align: center;
  }

  &__controls {
    flex: 1 1 520px;
    min-width: 260px;
  }

  &__tab-label {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }

  &__tab-count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    border-radius: 9px;
    background-color: var(--light-grey);
    color: var(--dark-grey);
    font-size: 0.7em;
  }

  // Three columns at full width — the 260px floor keeps the native time
  // input from clipping, so a capped viewport drops to two columns instead
  &__slots {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
    gap: 0.4rem;
    align-items: start;
  }

  &__slot {
    border: 1px solid var(--light-grey);
    border-radius: var(--border-radius-sm);

    &--selected {
      border-color: var(--primary);
    }

    &--dragover {
      outline: 2px dashed var(--primary);
      outline-offset: 2px;
    }

    &--dragover-invalid {
      outline: 2px dashed var(--error);
      outline-offset: 2px;
    }

    &--add {
      border-style: dashed;
      border-color: var(--grey);
      color: var(--dark-grey);
      cursor: pointer;

      &:hover {
        background-color: #f8f8f8;
      }
    }
  }

  &__slot-row {
    display: grid;
    grid-template-columns: $thumb-width 1fr 30px;
    align-items: center;
    gap: 0.6rem;
    padding: 0.4rem;
    cursor: pointer;

    @media (hover: hover) {
      &:hover .sunlight-dialog__slot-default,
      &:hover .sunlight-dialog__slot-image-remove,
      &:hover .sunlight-dialog__slot-image-download {
        opacity: 1;
        transform: translateY(0);
      }
    }
  }

  &__slot-default {
    background-color: transparent;
    color: white;
    box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.7);
    transition: opacity 150ms ease-out, transform 150ms ease-out;

    @media (hover: hover) {
      opacity: 0;
      transform: translateY(50%);
    }

    &--active {
      background-color: #f5b301;
      box-shadow: none;
      opacity: 1;
      transform: none;
    }
  }

  &__slot-thumb {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: $thumb-width;
    height: $thumb-height;
    flex-shrink: 0;
    border-radius: calc(var(--border-radius-sm) - 2px);
    background-color: var(--light-grey);

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
      border-radius: inherit;
    }

    &--dragover {
      outline: 2px dashed var(--primary);
      outline-offset: 2px;
    }

    &--dragover-invalid {
      outline: 2px dashed var(--error);
      outline-offset: 2px;
    }

    &--empty {
      flex-direction: column;
      gap: 0.15rem;
      border: 1px dashed var(--grey);
      background-color: white;
      color: var(--dark-grey);
      font-size: 0.8em;

      &:hover {
        background-color: #f8f8f8;
      }
    }

    &--add {
      color: var(--dark-grey);
      font-size: 0.8em;
    }

    &--uploading {
      opacity: 0.5;
      pointer-events: none;
    }
  }

  &__slot-actions {
    position: absolute;
    top: -5px;
    left: -5px;
    z-index: 1;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  &__slot-image-remove,
  &__slot-image-download {
    transition: opacity 150ms ease-out, transform 150ms ease-out;

    @media (hover: hover) {
      opacity: 0;
      transform: translateY(50%);
    }
  }

  &__slot-thumb-hint {
    font-size: 0.85em;
    color: var(--grey);
  }

  &__slot-time {
    display: flex;
    align-items: center;
    min-width: 0;

    input {
      max-width: 100%;
      padding: 0.15rem 0.3rem;
      border: 1px solid transparent;
      border-radius: var(--border-radius-sm);
      background: transparent;
      font-family: inherit;
      font-size: 0.95em;
      font-weight: 500;
      outline: none;
      cursor: pointer;

      &:hover,
      &:focus {
        border-color: var(--grey);
        background-color: white;
      }

      &::-webkit-calendar-picker-indicator {
        cursor: pointer;
      }
    }
  }

  &__slot-delete {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    border: none;
    border-radius: var(--border-radius-sm);
    background: transparent;
    color: var(--dark-grey);
    cursor: pointer;

    &:hover {
      background-color: var(--light-grey);
      color: var(--error);
    }
  }

  &__slot-warning {
    margin: 0;
    padding: 0 0.5rem 0.4rem;
    font-size: 0.8em;
    color: var(--error);
  }

  &__slot-add-label {
    min-width: 0;
    font-size: 0.9em;
  }

  &__slot-add-placeholder {
    justify-self: center;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background-color: var(--light-grey);
  }

  &__footer-warning {
    align-self: center;
    font-size: 0.8em;
    color: var(--error);
  }

  &__frame-count {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    min-height: 1em;
    font-size: 0.75em;
    color: var(--dark-grey);

    &--complete {
      color: var(--success);
    }

    &--partial {
      color: var(--warning);
    }
  }
}
</style>
